/**
 *  @file       WorkerPanel.jsx
 *  @author     Eddie Roosenmaallen <eddie@kingsds.network>
 *  @author     Kirill Kirnichansky <kirill@kingsds.network>
 *
 *  @date       June 2022
 *
 *  This panel presents a Worker interface which looks like the old Portal
 *  worker, but tied into the global portalWorker API
 */

import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDCPWorker } from '@/hooks/useDCPWorker';
import { debugging } from '@/lib/debugging';
import { AccountsContext, AccountSelect, Balance } from '@/features/accounts';
import { Button } from '@/components/Elements';
import { CreditsValueTable, WorkerLauncherContent } from '@/features/brands';
import { WorkerDebugDialog } from '../WorkerDebugDialog';
import { StatCard } from './StatCard';

import './WorkerPanel.css';

/**
 *  Render a full-window browser worker UX
 *
 *  @param  {object}      _props Expected properties from parent
 *  @return {JSX.Element}        The rendered component.
 */
export function WorkerPanel(_props)
{
  const { t } = useTranslation();
  const accountsRef = useRef();
  const cpuCoresInputRef = useRef();
  const accounts = useContext(AccountsContext);

  if (!accounts?.[0])
    throw new Error('No account to deposit credits into', );

  const {
    worker,
    workerState,
    workerStatistics,
    toggleWorker,
  } = useDCPWorker({
    workerOptions: {
      paymentAddress: accounts[0].address,
    },
  });

  // computeTime is in seconds, formatTimeInterval expects ms.
  const computeTime = formatTimeInterval(workerStatistics.computeTime * 1000);

  /**
   * Variable that holds the overall total earned credits of a specific account. This value is displayed on the
   * smith-rs portal and is calculated by adding the current session credits to the initial balance of
   * the paying account (smith-rs portal users have only one account) upon render.
   */
  const cumulativeCredits = accounts[0].balance.plus(workerStatistics.credits);

  let status = t('status_ready');

  if (workerState.working && workerState.workingSandboxes <= 0)
    status = t('status_waiting');
  else if (workerState.working)
    status = t('status_working');

  if (workerState.fetching)
    status = t('status_fetching');

  if (workerState.submitting)
    status = t('status_submitting');

  if (workerState.serviceError)
    status = t('status_error');

  const updatePaymentAddress = () => {
    worker.workerOptions.paymentAddress = accountsRef.current.value;
  };

  const workButtonLabel = (() => {
    if (workerState.willWork === true)
      return t('starting');
    else if (workerState.willWork === false)
      return t('stopping');

    return workerState.working ? t('stop_this_session') : t('start');
  })();

  const setCoresInputBorder = () => {
    let borderColor = false;    // default

    if (cpuCoresInputRef.current.value > (navigator.hardwareConcurrency * 2))
      borderColor = '#b83b33';  // 2 times over hardwareConcurrency -> red
    else if (cpuCoresInputRef.current.value > navigator.hardwareConcurrency)
      borderColor = '#eb9f22';  // over hardwareConcurrency -> orange

    cpuCoresInputRef.current.style.border = borderColor ? `2.5px solid ${borderColor}` : '';

    // display note if over hardwareConcurrency
    const workerOptionsNote = document.getElementById('workerOptionNote');
    if (!borderColor)
      workerOptionsNote.style.display = 'none';
    else
    {
      workerOptionsNote.style.display = 'block';
      workerOptionsNote.style.color = borderColor;
    }
  }

  let typingTimeout;
  const cpuCoresChangeHandler = () => {
    // force max 3 decimal places
    if (cpuCoresInputRef.current.value.split('.')[1] && cpuCoresInputRef.current.value.split('.')[1].length > 1)
      cpuCoresInputRef.current.value = parseFloat(cpuCoresInputRef.current.value).toFixed(1);
    // force 3 digits before decimal
    if (cpuCoresInputRef.current.value.split('.')[0].length > 3) {
      const split = cpuCoresInputRef.current.value.split('.');
      let val = split[0].slice(0, 3);
      if (split[1]) val += '.' + split[1];
      cpuCoresInputRef.current.value = val;
    }

    setCoresInputBorder();

    clearTimeout(typingTimeout);
    typingTimeout = setTimeout(() => {
      if (worker)
      {
        // if val <= 0, reset input back to workerOptions.cores.cpu
        if (parseFloat(cpuCoresInputRef.current.value) >= 0)
          worker.workerOptions.cores.cpu = parseFloat(cpuCoresInputRef.current.value);
        else
        {
          cpuCoresInputRef.current.value = worker.workerOptions.cores.cpu;
          setCoresInputBorder();
        }
      }
    }, 700);
  }

  useEffect(() => {
    if (worker)
      cpuCoresInputRef.current.value = worker.workerOptions.cores.cpu;
    setCoresInputBorder();

    /* If we are in the smith portal, set cores in worker to 1. */
    if (worker && window.location.hostname.split('.')[0] === 'smith-rs')
      worker.workerOptions.cores.cpu = 1;
  }, [worker]);

  return (
    <div id="workerWindow">
      <h1>{t("dcp_web_worker")}</h1>
      <div className="container">
        <div id="topRow">
          <div className="workerLauncher card">
            <div className="toggleWork">
              <WorkerLauncherContent />
              <p>{t("worker_page_par")}</p>
              {/* responsiveness - Button only visible @ width > 1500 */}
              <Button
                id="toggleWorkBtn1"
                onClick={() => toggleWorker()}
                colour
                text={workButtonLabel}
              />{" "}
            </div>
            <div className="workOptions">
              <div className="contributorAccountDescription">
                <p>{t("contributor_account")}</p>
              </div>
              <h4>{t("select_account_for_depositing_credits")}</h4>
              <AccountSelect ref={accountsRef} accounts={accounts} selected={worker ? worker.workerOptions.paymentAddress.address : ''} onChange={updatePaymentAddress} />
              <h4>{t("estimated_number_of_cpu_cores")}</h4>
              <div className="flex">
                <input id="cpuCoresInput" ref={cpuCoresInputRef} onChange={cpuCoresChangeHandler} type="number" min="0" max="999" step="0.1"/>
                <p id="workerOptionNote">
                  <strong>{t("note")}</strong> {t("worker_page_note")}.
                </p>
              </div>
            </div>
            {/* responsiveness - Button only visible @ width < 1500 */}
            <Button
              id="toggleWorkBtn2"
              onClick={() => toggleWorker()}
              colour
              text={workButtonLabel}
            />{" "}
          </div>
          {/* credits to current dollar amount table - currently only rendered for smith-rs brand */}
          <CreditsValueTable />
        </div>
        <div className="workerStats">
          <StatCard id="creditStat" title={t("credits_earned")}>
            <h2 className='_smith_rs'>CURRENT SESSION</h2>
            <div className="statsContent">
              <Balance className="dashMetric" balance={workerStatistics.credits} />
            </div>
            <h2 className='_smith_rs'>OVERALL TOTAL EARNED</h2>
            <div className="_smith_rs statsContent">
              <Balance className="dashMetric" balance={cumulativeCredits} dark/>
            </div>
          </StatCard>
          <StatCard id="slicesStat" title={t("slices_completed")}>
            <h2 className="_smith_rs">CURRENT SESSION</h2>
            <div className="statsContent">
              <span className="dashMetric">{workerStatistics.slices.toLocaleString()}</span>
            </div>
          </StatCard>
          <StatCard id="timeStat" title={t("total_computing_time")}>
            <h2 className="_smith_rs">CURRENT SESSION</h2>
            <div className="timeContent">
              <div>
                <p>{t("days")}</p>
                <span className="dashMetric">{computeTime.days}</span>
              </div>
              <div>
                <p>{t("hours")}</p>
                <span className="dashMetric">{computeTime.hours}</span>
              </div>
              <div>
                <p>{t("minutes")}</p>
                <span className="dashMetric">{computeTime.minutes}</span>
              </div>
            </div>
          </StatCard>
        </div>
        <div className="workerCurrentStatus card">
          <div className="cardTitle currentStatusTitle">
            <h1>
              {t("current_status")}: {status}
            </h1>
          </div>
          {worker?.workingSandboxes.length > 0 ? (
            <ActiveSandboxes sandboxes={worker.workingSandboxes.map(s => s.sandboxHandle)} />
          ) : (
            <div>
              <h2 id="noWorkText">{t("no_work_to_display")}&#8230;</h2>
            </div>
          )}
        </div>
      </div>
      <WorkerDebugDialog workerOptions={worker?.workerOptions}/>
    </div>
  );
}

/**
 *  Convenience component to enwrap the status bars
 *
 *  @param {object}     props
 *  @param {Sandbox[]}  sandboxes   Array of working Sandboxes
 */
function ActiveSandboxes({ sandboxes })
{
  return (
  <div id="active-slices-container">{
    sandboxes
    .map(sandbox => (
      <SandboxProgress key={sandbox.id} sandbox={sandbox} />
    ))
  }
  </div>);
}

/**
 *  Progress bar for a single Sandbox which self-manages event listeners and
 *    progress updates
 *
 *  @param {object}   props
 *  @param {Sandbox}  props.sandbox   A DCP Worker Sandbox
 */
function SandboxProgress(props)
{
  const { t } = useTranslation();
  const { sandbox } = props;
  const { id } = sandbox;

  const [ name, setName ] = useState(sandbox.public.name);
  const [ description, setDescription ] = useState(sandbox.public.description);
  // const [ computeGroups, setComputeGroups ] = useState('');

  const [ progress, setProgress ] = useState(0);
  const [ indeterminate, setIndeterminate ] = useState(false);

  const onProgress = ev => {
    setProgress(ev);
    setIndeterminate(ev === undefined);
  }

  const onStart = ev => {
    debugging('worker') && console.debug(`283: sandbox${ev.sandbox.id}: start event:`, ev);
    setName(ev.name);
    setDescription(ev.description);
    // setComputeGroups(ev.job.computeGroups || ''); //NYI

    setProgress(0);
    setIndeterminate(false);
  }

  useEffect(() => {
    props.sandbox.on('progress', onProgress);
    props.sandbox.on('start', onStart);

    return () => {
      props.sandbox.off('progress', onProgress);
      props.sandbox.off('start', onStart);
    }
  });

  // render():
  const label = name || t('waiting_for_assignment');
  const title = description || t('discreetly_making_the_world_smarter');

  const progressClassName = "worker-sandbox-progress-container" + (indeterminate ? ' indeterminate' : '');
  const progressStyle = { width: `${progress}%` };

  return (
    <div id={`progressContainer-${id}`} className={progressClassName} title={title}>
      <div id={`progressBar-${id}`} className="worker-sandbox-progress-bar progress-bar-striped progress-bar-animated" style={progressStyle}>&nbsp;</div>
      <div id={`progressLabel-${id}`} className="worker-sandbox-progress-label">{label}</div>
    </div>
  );
}

/**
 * Format a time interval expressed in milliseconds into an object containing properites: days, hours, minutes
 * and seconds of the interval. The properties represent a portion of the time interval, not the full interval
 * representaion in the respective unit. The time interval is a combination of all values.
 *
 * @param    ms      The number of milliseconds in the time interval
 *
 * @returns the time interval as an object
 */
function formatTimeInterval(ms)
{
  let timeInterval = {
    days: '0',
    hours: '0',
    minutes: '0',
    seconds: '0',
  };

  ms = Math.floor(ms);

  if (ms >= 86400000)
  {
    timeInterval.days = Math.floor(ms / 86400000).toFixed(0);
    ms = ms % 86400000;
  }

  timeInterval.hours = (Math.floor(ms / 3600000) % 24).toString();
  timeInterval.minutes = (Math.floor(ms /   60000) % 60).toString();
  timeInterval.seconds = (Math.floor(ms /    1000) % 60).toFixed(1);

  return timeInterval
}
